import * as jp from 'jsonpath';
import {
  AccessAttributeValueWithAttribute,
  CreateAttributeValueByName
} from 'src/api/deinverband-api';
import {
  AttributeJsonSchema,
  AttributeValue,
  AttributeValueProperty,
  AttributesPatch
} from 'src/types/attributes';
import { Id } from 'src/types/common';
import { extractPropertyDefinitions } from './attributes';
import { filterUndefined } from './tools';

export const coalesceString = ([value]: unknown[]) => {
  if (typeof value === 'string') return value;
  if (value === null) return null;
  return value?.toString();
};

/**
 * Parse an access attribute with schema for generic rendering
 */
export const extractAttributeValue = ({
  id,
  attribute,
  value
}: AccessAttributeValueWithAttribute): AttributeValue | undefined => {
  // ensure attribute is valid
  if (!id) throw new Error('Invalid attribute value id');

  const attributeValue = JSON.parse(value);
  if (!attributeValue || typeof attributeValue !== 'object')
    throw new Error('Attribute should be top level object');

  // unwrap attribute using the schema
  const schema: AttributeJsonSchema = JSON.parse(attribute.jsonSchema);
  const propertiesDefinitions = extractPropertyDefinitions(schema);

  // map property definitions to values
  const properties = propertiesDefinitions.map(
    (definition): AttributeValueProperty => ({
      ...definition,
      value: coalesceString(jp.query(attributeValue, definition.path))
    })
  );

  return {
    format: 'value',
    id,
    attributeType: attribute.name,
    properties
  };
};

/**
 * Extract the local attribute values from API
 */
export const extractAttributeValues = (
  accessAttributes: AccessAttributeValueWithAttribute[]
) => filterUndefined(accessAttributes.map(extractAttributeValue));

/**
 * Build the create attribute values for the API
 */
export const createAttributeValues = (
  attributes: AttributesPatch
): CreateAttributeValueByName[] => {
  // extract valid added attributes (negative id)
  const added = (attributes.update || []).filter(
    ({ id }) => id && id < 0
  ) as (AttributeValue & { id: number })[];

  return added.map((attribute) => ({
    attributeName: attribute.attributeType,
    value: JSON.stringify(
      Object.fromEntries(
        attribute.properties.map((property) => [
          // HACK: parse JSON path
          property.path.substring(2),
          property.value
        ])
      )
    )
  }));
};

/**
 * Build the attribute values update for the API
 */
export const patchAttributeValues = (attributes: AttributesPatch) => {
  // extract valid added attributes (positive id)
  const replace = (attributes.update || []).filter(
    ({ id }) => id && id > 0
  ) as (AttributeValue & { id: number })[];

  return {
    // TODO: create attributes when backend supports it
    idsToAdd: [],
    idsToRemove: [
      // added properties have a negative id
      // (remove them from the update list)
      ...replace.map(({ id }) => id),
      ...(attributes.remove || [])
    ]
  };
};

export const buildEditorAttribute = (
  attributeId: number,
  editorId: Id
): AttributeValue => ({
  format: 'value',
  id: attributeId,
  attributeType: 'Editor',
  properties: [{ type: 'user', path: '$.editorId', value: editorId }]
});

export const buildAdminAttribute = (attributeId: number): AttributeValue => ({
  format: 'value',
  id: attributeId,
  attributeType: 'Admin',
  properties: []
});
